import ButtonComponent, { IconSize } from '@spa/components/button/button/button.component';
import { ReactElement, memo } from 'react';
import { ButtonType } from '@spa/components/button/types/ButtonType';
import ImageComponent from '@spa/components/image-component/image.component';
import classNames from 'clsx';
import styles from './alert.component.module.scss';

export enum AlertType {
  INFO,
  WARNING,
  ERROR,
  SUCCESS
}

export interface Props {
  readonly alertType: AlertType;
  readonly borderless?: boolean;
  readonly centeredAlignment?: boolean;
  readonly content: ReactElement | string;
  readonly hideIcon?: boolean;
  readonly onCloseClick?: () => void;
}

function AlertComponent({
  alertType,
  borderless,
  centeredAlignment,
  content,
  hideIcon,
  onCloseClick
}: Props): ReactElement {
  const alertClass = classNames(styles.alert, {
    [styles['alert--align-center']]: centeredAlignment,
    [styles['alert--borderless']]: borderless,
    [styles['alert--info']]: alertType === AlertType.INFO,
    [styles['alert--warning']]: alertType === AlertType.WARNING,
    [styles['alert--error']]: alertType === AlertType.ERROR,
    [styles['alert--success']]: alertType === AlertType.SUCCESS
  });

  const iconClass = classNames(styles.alert__icon, {
    [styles['alert__icon--info']]: alertType === AlertType.INFO,
    [styles['alert__icon--warning']]: alertType === AlertType.WARNING,
    [styles['alert__icon--error']]: alertType === AlertType.ERROR,
    [styles['alert__icon--success']]: alertType === AlertType.SUCCESS
  });

  const alertKey: { [key: number]: string } = {
    [AlertType.ERROR]: 'error',
    [AlertType.INFO]: 'info',
    [AlertType.SUCCESS]: 'success',
    [AlertType.WARNING]: 'warning'
  };

  return (
    <div className={alertClass} role="alert">
      {!hideIcon && (
        <ImageComponent
          className={iconClass}
          src={`/assets/icons/alert-${alertKey[alertType]}.svg`}
          alt={alertKey[alertType]}
          width={24}
          height={24}
          unoptimized
        />
      )}
      <div
        className={styles['alert__content']}
        role="alertdialog"
        aria-label="alert message"
        aria-modal="true"
        aria-live="polite"
      >
        {content}
      </div>
      {onCloseClick && (
        <div className={styles['alert__close']}>
          <ButtonComponent
            buttonType={ButtonType.AS_TEXT}
            iconLeft={{
              src: '/assets/icons/close-icon.svg',
              width: 30,
              alt: '',
              height: 30,
              unoptimized: true
            }}
            iconSize={IconSize.SMALL}
            onClick={(): void => onCloseClick()}
          />
        </div>
      )}
    </div>
  );
}

export default memo(AlertComponent);
